import * as React from 'react';
import { getMouseCanvas, GenericChartComponent } from '@react-financial-charts/core';
import type { MoreProps } from '@/types/commontypes';

export interface ClickableReactangleProps {
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly strokeWidth: number;
  readonly strokeStyle: string;
  readonly fillStyle: string;
  readonly r: number;
  readonly cx?: number;
  readonly cy?: number;
  readonly className: string;
  readonly show: boolean;
  readonly interactiveCursorClass?: string;
  readonly xyProvider?: (moreProps: MoreProps) => number[];
  readonly width?: number;
  readonly height?: number;
  readonly cornerRadius?: number;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
}

export class ClickableReactangle extends React.Component<ClickableReactangleProps> {
  public static defaultProps = {
    className: 'react-financial-charts-interactive-line-edge',
    show: false,
  };

  public render() {
    const {
      interactiveCursorClass,
      onDragStart,
      onDrag,
      onDragComplete,
      show,
      onDoubleClick: _onDoubleClick,
    } = this.props;

    if (!show) {
      return null;
    }

    return (
      <GenericChartComponent
        interactiveCursorClass={interactiveCursorClass}
        selected
        isHover={this.isHover}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        canvasDraw={this.drawOnCanvas}
        canvasToDraw={getMouseCanvas}
        drawOn={['pan', 'mousemove', 'drag']}
        onDoubleClick={this.handleDoubleClick}
      />
    );
  }

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const { strokeStyle, strokeWidth, fillStyle, r } = this.props;

    ctx.lineWidth = strokeWidth;
    ctx.fillStyle = fillStyle;
    ctx.strokeStyle = strokeStyle;

    const [x, y] = this.helper(moreProps);
    const width = r * 2;
    const height = r * 2;
    const radius = r / 2;

    this.roundRect(ctx, x - r, y - r, width, height, radius);
    ctx.fill();
    ctx.stroke();
  };

  private readonly isHover = (moreProps: MoreProps) => {
    const { mouseXY } = moreProps;
    const {
      chartConfig: { height },
    } = moreProps;

    // 마우스가 차트 섹션 밖이면 호버 불가 (서브차트 간 이동 시 호버 고착 방지)
    if (mouseXY[1] < 0 || mouseXY[1] > height) return false;

    const r = this.props.r + 7;
    const [x, y] = this.helper(moreProps);

    const [mx, my] = mouseXY;
    const hover = x - r < mx && mx < x + r && y - r < my && my < y + r;

    return hover;
  };

  private readonly helper = (moreProps: MoreProps) => {
    const { xyProvider, cx, cy } = this.props;

    if (xyProvider !== undefined) {
      return xyProvider(moreProps);
    }

    const {
      xScale,
      chartConfig: { yScale },
    } = moreProps;

    const x = xScale(cx ?? 0);
    const y = yScale(cy ?? 0);
    return [x, y];
  };

  private readonly roundRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  private readonly handleDoubleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    if (this.isHover(moreProps)) {
      const { onDoubleClick } = this.props;
      if (onDoubleClick) {
        onDoubleClick(e, moreProps);
      }
    }
  };
}
